import { createSignal } from "solid-js"
import type { WorktreeDescriptor } from "../../../server/src/api-types"
import { serverApi } from "../lib/api-client"
import { serverEvents } from "../lib/server-events"
import { getSessionRoot, sessions } from "./session-state"
import { getLogger } from "../lib/logger"
import type { WorktreeReadyEvent } from "../lib/sse-manager"
import { showToastNotification } from "../lib/notifications"
import { tGlobal } from "../lib/i18n"
import { normalizeSessionDirectory } from "./session-list-options"
import { prioritizedRead } from "../lib/prioritized-read"
import { activeInstanceId } from "./instances"

const log = getLogger("api")

const [worktreesByInstance, setWorktreesByInstance] = createSignal<Map<string, WorktreeDescriptor[]>>(new Map())
const [gitRepoStatusByInstance, setGitRepoStatusByInstance] = createSignal<Map<string, boolean | null>>(new Map())

const worktreeRequests = new Map<string, Promise<void>>()
const pendingWorktreeRefreshes = new Set<string>()
const freshWorktreeRequests = new Map<string, Promise<void>>()
const worktreeReads = new Map<string, Promise<void>>()
const worktreeReadyRefreshes = new Map<string, Promise<void>>()
const familyMoveRequests = new Map<string, Promise<void>>()
const unreconciledFamilyMoves = new Map<string, { instanceId: string; directories: string[]; settled: boolean; request: Promise<void> }>()
const [pendingFamilyMoveSlugs, setPendingFamilyMoveSlugs] = createSignal(new Map<string, string>())
const defaultDirectories = new Map<string, string>()

type WorktreeReadyRefresh = (instanceId: string) => Promise<void>

async function queueWorktreeRequest(instanceId: string, initial: boolean, fresh = false): Promise<void> {
  const requests = fresh ? freshWorktreeRequests : worktreeRequests
  const existing = requests.get(instanceId)
  if (existing) {
    if (!initial && !fresh) pendingWorktreeRefreshes.add(instanceId)
    return existing
  }
  const load = (initialRead: boolean) => {
    // Display and required-fresh callers have independent outcomes, but every
    // read/publication shares one lane. An older cached response cannot land last.
    const previous = worktreeReads.get(instanceId)
    const read = (previous?.catch(() => undefined) ?? Promise.resolve()).then(async () => {
      try {
        const response = await prioritizedRead(
          () => activeInstanceId() === instanceId,
          new AbortController().signal,
          () => serverApi.fetchWorktrees(instanceId, fresh),
        )
        if (response.defaultDirectory) defaultDirectories.set(instanceId, response.defaultDirectory)
        else defaultDirectories.delete(instanceId)
        setWorktreesByInstance((prev) => {
          const next = new Map(prev)
          next.set(instanceId, response.worktrees ?? [])
          return next
        })

        setGitRepoStatusByInstance((prev) => {
          const next = new Map(prev)
          next.set(instanceId, typeof response.isGitRepo === "boolean" ? response.isGitRepo : null)
          return next
        })
      } catch (error) {
        log.warn(initialRead ? "Failed to load worktrees" : "Failed to reload worktrees", { instanceId, error })
        if (!initialRead || fresh) throw error
        // A fresh read may have published while this initial display read waited.
        if (worktreesByInstance().has(instanceId)) return

        setWorktreesByInstance((prev) => {
          const next = new Map(prev)
          next.set(instanceId, [])
          return next
        })

        // Preserve any previous value; if unknown, keep it unknown.
        setGitRepoStatusByInstance((prev) => {
          if (prev.has(instanceId)) return prev
          const next = new Map(prev)
          next.set(instanceId, null)
          return next
        })
      }
    })
    worktreeReads.set(instanceId, read)
    return read.finally(() => {
      if (worktreeReads.get(instanceId) === read) worktreeReads.delete(instanceId)
    })
  }
  // Like the provider/model catalogue, retain one in-flight read and one dirty
  // bit for display demand. Concurrent fresh callers share only their fresh read;
  // its failure must neither satisfy fresh recovery nor discard display demand.
  const task = Promise.resolve().then(async () => {
    let initialRead = initial
    do {
      if (!fresh) pendingWorktreeRefreshes.delete(instanceId)
      try {
        await load(initialRead)
      } catch (error) {
        if (fresh || !pendingWorktreeRefreshes.has(instanceId)) throw error
      }
      initialRead = false
    } while (!fresh && pendingWorktreeRefreshes.has(instanceId))
  })

  requests.set(instanceId, task)
  await task.finally(() => {
    if (requests.get(instanceId) === task) {
      requests.delete(instanceId)
    }
  })
}

async function ensureWorktreesLoaded(instanceId: string): Promise<void> {
  if (!instanceId) return
  if (worktreesByInstance().has(instanceId) && gitRepoStatusByInstance().has(instanceId)) return

  const existing = worktreeRequests.get(instanceId)
  if (existing) {
    await existing
    if (worktreesByInstance().has(instanceId) && gitRepoStatusByInstance().has(instanceId)) return
  }

  await queueWorktreeRequest(instanceId, true)
}

async function reloadWorktrees(instanceId: string, fresh = false): Promise<void> {
  if (!instanceId) return
  await queueWorktreeRequest(instanceId, false, fresh)
}

serverEvents.on("workspace.worktreesChanged", (event) => {
  if (event.type !== "workspace.worktreesChanged") return
  const id = event.workspaceId
  // Refresh consumers that already requested this inventory. Queue behind an
  // older HTTP response so it cannot overwrite the completed background scan.
  if (!worktreesByInstance().has(id) && !worktreeRequests.has(id) && !freshWorktreeRequests.has(id)) return
  void reloadWorktrees(id).catch(error => log.warn("Failed to receive refreshed worktrees", { instanceId: id, error }))
})

async function handleWorktreeReady(
  instanceId: string,
  event: WorktreeReadyEvent,
  refreshWorktrees: WorktreeReadyRefresh = reloadWorktrees,
): Promise<void> {
  if (!instanceId) return

  log.info("OpenCode worktree ready", {
    instanceId,
    directory: event.location?.directory,
    name: event.data?.name,
  })

  const previous = worktreeReadyRefreshes.get(instanceId)
  const task = (previous?.catch(() => undefined) ?? Promise.resolve()).then(async () => {
    await refreshWorktrees(instanceId)
  })

  worktreeReadyRefreshes.set(instanceId, task)
  await task.finally(() => {
    if (worktreeReadyRefreshes.get(instanceId) === task) {
      worktreeReadyRefreshes.delete(instanceId)
    }
  })
}

function getGitRepoStatus(instanceId: string): boolean | null {
  return gitRepoStatusByInstance().get(instanceId) ?? null
}

export function getDirectoryOnlyWorktree(instanceId: string): WorktreeDescriptor | undefined {
  return getWorktrees(instanceId).find(worktree => worktree.slug === "root" && worktree.directoryOnly)
}

async function createWorktree(instanceId: string, slug: string, fromSlug = "root"): Promise<{ slug: string; directory: string; branch?: string }> {
  if (!instanceId) {
    throw new Error("Missing instanceId")
  }
  const trimmed = (slug ?? "").trim()
  if (!trimmed) {
    throw new Error("Worktree name is required")
  }
  return serverApi.createWorktree(instanceId, { slug: trimmed, fromSlug })
}

async function deleteWorktree(
  instanceId: string,
  slug: string,
  options?: { force?: boolean },
  refreshSessions: (instanceId: string) => Promise<void> = (id) =>
    import("./session-api").then(({ fetchSessions }) => fetchSessions(id, { reset: true, strictStatus: true })),
): Promise<void> {
  if (!instanceId) {
    throw new Error("Missing instanceId")
  }
  const trimmed = (slug ?? "").trim()
  if (!trimmed || trimmed === "root") {
    throw new Error("Invalid worktree")
  }
  let deleteError: unknown
  try {
    await serverApi.deleteWorktree(instanceId, trimmed, options)
  } catch (error) {
    deleteError = error
  }
  const refreshers = [() => reloadWorktrees(instanceId), () => refreshSessions(instanceId)]
  const refreshes = await Promise.allSettled(refreshers.map((refresh) => refresh()))
  const failed = refreshes.flatMap((refresh, index) => refresh.status === "rejected" ? [index] : [])
  if (failed.length) {
    const retries = await Promise.allSettled(failed.map((index) => refreshers[index]!()))
    for (const retry of retries) {
      if (retry.status === "rejected") {
        log.warn("Failed to refresh after worktree deletion", { instanceId, slug: trimmed, error: retry.reason })
      }
    }
  }
  if (deleteError) {
    throw deleteError
  }
}

function getWorktrees(instanceId: string): WorktreeDescriptor[] {
  return worktreesByInstance().get(instanceId) ?? []
}

function normalizeWorktreeSlug(instanceId: string, slug: string): string {
  return (slug ?? "").trim() || "root"
}

function getDefaultWorktreeSlug(instanceId: string): string {
  return normalizeWorktreeSlug(instanceId, "root")
}

function getParentSessionId(instanceId: string, sessionId: string): string {
  return getSessionRoot(instanceId, sessionId)?.id ?? sessionId
}

function normalizeDirectory(directory: string): string {
  return normalizeSessionDirectory(directory)
}

/** Matches a native session location against the worktree inventory. */
function getWorktreeForDirectory(instanceId: string, directory: string): WorktreeDescriptor | undefined {
  const normalized = normalizeDirectory(directory)
  return getWorktrees(instanceId).find((worktree) => normalizeDirectory(worktree.serviceDirectory ?? worktree.directory) === normalized)
}

function getWorktreeSlugForParentSession(instanceId: string, parentSessionId: string): string {
  const directory = sessions().get(instanceId)?.get(parentSessionId)?.location.directory
  const locationSlug = directory && getWorktreeForDirectory(instanceId, directory)?.slug
  if (locationSlug) return normalizeWorktreeSlug(instanceId, locationSlug)

  return "root"
}

function getWorktreeSlugForSession(instanceId: string, sessionId: string): string {
  return getWorktreeSlugForParentSession(instanceId, sessionId)
}

export function getDefaultWorktreeDirectory(instanceId: string): string | undefined {
  return defaultDirectories.get(instanceId)
}

export function getPendingWorktreeSlug(instanceId: string, sessionId: string): string | undefined {
  return pendingFamilyMoveSlugs().get(`${instanceId}:${getParentSessionId(instanceId, sessionId)}`)
}

export function getPendingWorktreeLocations(instanceId: string): Array<{ directory: string }> {
  return [...unreconciledFamilyMoves.values()].filter(move => move.instanceId === instanceId)
    .flatMap(move => move.directories.map(directory => ({ directory })))
}

export function clearPendingWorktreeMoves(instanceId: string): void {
  const keys = [...unreconciledFamilyMoves].filter(([, move]) => move.instanceId === instanceId).map(([key]) => key)
  for (const key of keys) unreconciledFamilyMoves.delete(key)
  setPendingFamilyMoveSlugs(previous => {
    const next = new Map(previous)
    for (const key of keys) next.delete(key)
    return next
  })
}

/** Only a complete owned project inventory plus fresh activity can release an
 * uncertain move's locations. Capture before I/O so an older read cannot settle
 * a newer or still-running move. Absence from that scope is authoritative too;
 * deleted or moved-away member IDs must not pin these directories forever.
 * This never retries a mutation and does not own selector busy state. */
export function captureWorktreeMoveReconciliation(instanceId: string) {
  const captured = [...unreconciledFamilyMoves].filter(([, move]) => move.instanceId === instanceId && move.settled)
  return () => {
    for (const [key, move] of captured) {
      if (unreconciledFamilyMoves.get(key) !== move || (familyMoveRequests.has(key) && familyMoveRequests.get(key) !== move.request)) continue
      unreconciledFamilyMoves.delete(key)
    }
  }
}

async function setWorktreeSlugForParentSession(
  instanceId: string,
  parentSessionId: string,
  slug: string,
  options: {
    currentSlug?: string
    moveFamily?: (instanceId: string, rootSessionId: string, worktreeSlug: string) => Promise<unknown>
    refreshSessions?: (instanceId: string) => Promise<void>
  } = {},
): Promise<void> {
  await ensureWorktreesLoaded(instanceId)
  const rootSessionId = getParentSessionId(instanceId, parentSessionId)
  const normalizedSlug = normalizeWorktreeSlug(instanceId, slug)
  const worktree = getWorktrees(instanceId).find((candidate) => candidate.slug === normalizedSlug)
  if (!worktree) throw new Error(`Worktree not found: ${normalizedSlug}`)

  // Controlled selectors can report their current option while metadata loads.
  // Do not turn that reconciliation into a family move and native move echoes.
  const currentDirectory = sessions().get(instanceId)?.get(rootSessionId)?.location.directory
  const targetDirectory = worktree.serviceDirectory ?? worktree.directory
  if (currentDirectory && normalizeDirectory(currentDirectory) === normalizeDirectory(targetDirectory)) return

  const key = `${instanceId}:${rootSessionId}`
  const previous = familyMoveRequests.get(key)
  const moveFamily = options.moveFamily ?? ((id: string, sessionId: string, worktreeSlug: string) =>
    serverApi.moveSessionFamily(id, sessionId, { worktreeSlug }))
  const refreshSessions = options.refreshSessions ?? ((id: string) =>
    import("./session-api").then(({ fetchSessions }) => fetchSessions(id, { reset: true, strictStatus: true })))
  const task: Promise<void> = (previous?.catch(() => undefined) ?? Promise.resolve()).then(async () => {
    const family = [...(sessions().get(instanceId)?.values() ?? [])].filter(session => getParentSessionId(instanceId, session.id) === rootSessionId)
    const prior = unreconciledFamilyMoves.get(key)
    const recovery = { instanceId,
      directories: [...new Set([targetDirectory, ...family.map(session => session.location.directory), ...(prior?.directories ?? [])])], settled: false, request: task }
    unreconciledFamilyMoves.set(key, recovery)
    let moveError: unknown
    try {
      await moveFamily(instanceId, rootSessionId, normalizedSlug)
    } catch (error) {
      moveError = error
    }
    recovery.settled = true
    try {
      await refreshSessions(instanceId)
    } catch {
      await refreshSessions(instanceId).catch((error) => {
        log.warn("Failed to refresh sessions after family move", { instanceId, rootSessionId, error })
      })
    }
    if (moveError) {
      log.warn("Failed to move session family", { instanceId, rootSessionId, error: moveError })
      showToastNotification({ category: "files",
        message: tGlobal("sessionList.worktreeMove.error"),
        variant: "error",
      })
      throw moveError
    }
  })

  familyMoveRequests.set(key, task)
  setPendingFamilyMoveSlugs(previous => new Map(previous).set(key, normalizedSlug))
  await task.finally(() => {
    if (familyMoveRequests.get(key) !== task) return
    familyMoveRequests.delete(key)
    setPendingFamilyMoveSlugs(previous => {
      const next = new Map(previous)
      next.delete(key)
      return next
    })
  })
}

export {
  worktreesByInstance,
  gitRepoStatusByInstance,
  ensureWorktreesLoaded,
  reloadWorktrees,
  handleWorktreeReady,
  getGitRepoStatus,
  getWorktrees,
  getDefaultWorktreeSlug,
  getParentSessionId,
  getWorktreeForDirectory,
  getWorktreeSlugForParentSession,
  getWorktreeSlugForSession,
  setWorktreeSlugForParentSession,
  createWorktree,
  deleteWorktree,
}
